import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import './HabitTrackingRow.scss';
import { createHabitTracked, deleteHabitTracked } from '~/Redux/Modules/HabitTrackeds/actions';

const HabitTrackingRow = ({
  daysArray,
  position,
  isCurrentMonthAndYear,
  currentDate,
  habit,
  trackedHabits,
  start,
  user,
}) => {
  const [localTrackedHabits, setLocalTrackedHabits] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    const habitsTrackedData = trackedHabits.reduce((acc, trackedHabit) => {
      const habitId = trackedHabit.habit_id;
      const trackDate = moment(trackedHabit.track_date).format('YYYY-MM-DD');

      if (!acc[habitId]) {
        acc[habitId] = {};
      }

      acc[habitId][trackDate] = trackedHabit;
      return acc;
    }, {});

    setLocalTrackedHabits(habitsTrackedData);
  }, [trackedHabits]);

  const isPastOrToday = (date, timezone) => {
    const now = moment().utcOffset(timezone, true);
    const targetDate = moment(date).utcOffset(timezone, true);

    // Set the time of both the target date and now to the end of the day
    targetDate.endOf('day');
    now.endOf('day');

    return targetDate.isSameOrBefore(now);
  };

  const getTrackedHabit = (date, habit) => {
    const formattedDate = moment(date).format('YYYY-MM-DD HH:mm:ss'); // Include time information

    return trackedHabits.find(
      trackedHabit =>
        trackedHabit.track_date === formattedDate && trackedHabit.habit_id === habit.id
    );
  };

  const handleTrackHabit = (date, habit, start, end) => {
    const trackedHabit = getTrackedHabit(date, habit);

    if (trackedHabit) {
      dispatch(deleteHabitTracked(trackedHabit, start, end));
    } else {
      const newHabitTracked = {
        id: 'temp-id',
        track_date: date,
        userId: user.id,
        habit_id: habit.id,
      };
      dispatch(createHabitTracked(newHabitTracked, start, end));
    }
  };

  const getStreakInfo = (date, habit, localTrackedHabits, daysArray) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    const habitIdString = habit.id.toString();
    const trackedHabit = localTrackedHabits[habitIdString]?.[formattedDate];

    const index = daysArray.indexOf(moment(date).date());
    const previousDate =
      index > 0
        ? moment(date)
            .subtract(1, 'days')
            .toDate()
        : null;
    const nextDate =
      index < daysArray.length - 1
        ? moment(date)
            .add(1, 'days')
            .toDate()
        : null;

    const nextDayTrackedHabit = nextDate
      ? localTrackedHabits[habitIdString]?.[
          moment(date)
            .add(1, 'days')
            .format('YYYY-MM-DD')
        ]
      : null;
    const previousDayTrackedHabit = previousDate
      ? localTrackedHabits[habitIdString]?.[
          moment(date)
            .subtract(1, 'days')
            .format('YYYY-MM-DD')
        ]
      : null;

    const isPartOfStreak = trackedHabit && (nextDayTrackedHabit || previousDayTrackedHabit);

    let streakPosition = null;
    if (isPartOfStreak) {
      if (nextDayTrackedHabit && previousDayTrackedHabit) {
        streakPosition = 'track-center';
      } else if (previousDayTrackedHabit) {
        streakPosition = 'track-before';
      } else if (nextDayTrackedHabit) {
        streakPosition = 'track-after';
      }
    }

    return { isPartOfStreak: isPartOfStreak, streakPosition: streakPosition };
  };

  const rowClassName =
    position === 'left'
      ? 'habit-tracking-row-left'
      : position === 'right'
      ? 'habit-tracking-row-right'
      : position === 'center'
      ? 'habit-tracking-row-center'
      : 'habit-tracking-row';
  return (
    <div className={rowClassName}>
      {daysArray.map(day => {
        const startDate = moment(start).startOf('month');
        const endDate = moment(start).endOf('month');
        const currentDate = moment(startDate).add(day - 1, 'days');
        const trackDate = currentDate.format('YYYY-MM-DD');
        const isHabitTracked = habit && localTrackedHabits[habit.id]?.[trackDate];
        const timeZone = start.utcOffset();
        const isDayPastOrToday = isPastOrToday(currentDate, timeZone);
        let isPartOfStreak, streakPosition;
        if (habit) {
          const streakInfo = getStreakInfo(currentDate, habit, localTrackedHabits, daysArray);
          isPartOfStreak = streakInfo.isPartOfStreak;
          streakPosition = streakInfo.streakPosition;
        } else {
          isPartOfStreak = false;
          streakPosition = null;
        }

        return (
          <div
            key={day}
            className={cn('tracking-day-column', { [streakPosition]: isPartOfStreak })}
          >
            <div
              className={cn('tracking-circle', {
                tracked: isHabitTracked,
                'not-tracked': !isHabitTracked,
              })}
              style={{
                backgroundColor: isHabitTracked ? habit.color : 'transparent',
                color: isHabitTracked ? habit.color : 'transparent',
                cursor: isDayPastOrToday ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (isDayPastOrToday) {
                  handleTrackHabit(trackDate, habit, startDate, endDate);
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

HabitTrackingRow.propTypes = {
  daysArray: propTypes.array,
  position: propTypes.string,
  isCurrentMonthAndYear: propTypes.bool,
  currentDate: propTypes.instanceOf(Date),
  habit: propTypes.object,
  trackedHabits: propTypes.array,
  start: propTypes.object,
  user: propTypes.oneOfType([propTypes.bool, propTypes.object]),
};

const mapStateToProps = ({ User, HabitTrackeds }) => {
  return {
    trackedHabits: HabitTrackeds.habitTrackedCollection.habitsTracked,
    user: User.data,
  };
};

export default connect(mapStateToProps)(HabitTrackingRow);
